import React, { createContext, useState, useContext, useEffect } from 'react';
import { GetMe, refreshAuthToken, login as loginService, loginWithGoogle as loginWithGoogleService, fetchGeneralSettings } from '../services/Http/Api';
import { useNavigate, useLocation } from 'react-router-dom';
import i18n from '../lang/i18n';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const isTokenExpired = (token) => {
    if (!token) return true;
    const decoded = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    return decoded.exp < now; // Check if the token is expired
};

const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('jwt_token');
            if (token && !isTokenExpired(token)) {
                try {
                    const userData = await GetMe();
                    setUser(userData);
                    setIsAuthenticated(true);
                    await applyOrganizationLanguage(userData);
                } catch (error) {
                    console.error('Failed to fetch user data during initialization:', error);
                    setIsAuthenticated(false);
                    localStorage.removeItem('jwt_token'); // Clear invalid token
                }
            } else if (token && isTokenExpired(token)) {
                try {
                    const newToken = await refreshAuthToken();
                    if (newToken) {
                        localStorage.setItem('jwt_token', newToken);
                        const userData = await GetMe();
                        setUser(userData);
                        setIsAuthenticated(true);
                        await applyOrganizationLanguage(userData);
                    }
                } catch (error) {
                    console.error('Failed to refresh token during initialization:', error);
                    setIsAuthenticated(false);
                    localStorage.removeItem('jwt_token');
                }
            }
            setLoading(false);
        };

        initializeAuth();
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            // Auth stranice ne smiju pregaziti odredisnu putanju. Posebno je
            // vazno sacuvati /connect/... dok korisnik potvrduje e-mail.
            if (!['/login', '/register', '/forgot-password', '/verify-email'].includes(location.pathname)) {
                localStorage.setItem('lastVisitedPath', `${location.pathname}${location.search}`);
            }
        }
    }, [isAuthenticated, location.pathname, location.search]);

    const refreshUser = async () => {
        try {
            const userData = await GetMe();
            setUser(userData);
        } catch (error) {
            console.error('Failed to refresh user data:', error);
        }
    };

    const loginWithToken = async (token) => {
        if (!token) {
            throw new Error('Missing token');
        }

        localStorage.setItem('jwt_token', token);
        const userData = await GetMe();
        setUser(userData);
        await applyOrganizationLanguage(userData);

        setIsTransitioning(true);
        setIsAuthenticated(true);
        setIsTransitioning(false);

        const savedPath = localStorage.getItem('lastVisitedPath') || '/';
        const targetPath = userData.retailer_onboarding?.required && !savedPath.startsWith('/connect/') ? '/setup' : savedPath;
        return { userData, targetPath };
    };

    const login = async (email, password) => {
        try {
            const data = await loginService(email, password);
            return await loginWithToken(data.token);
        } catch (error) {
            setIsTransitioning(false);
            throw new Error('Login failed');
        }
    };

    const loginWithGoogle = async (idToken) => {
        try {
            const data = await loginWithGoogleService(idToken);
            return await loginWithToken(data.token);
        } catch (error) {
            setIsTransitioning(false);
            throw new Error(error?.response?.data?.message || 'Google login failed');
        }
    };

    const applyOrganizationLanguage = async (userDataOverride = null) => {
        try {
            const resolvedUser = userDataOverride || user;
            if (resolvedUser?.is_system_admin) {
                return;
            }
            const settings = await fetchGeneralSettings();
            const orgLang = settings?.language?.iso_code || settings?.language?.code;
            if (orgLang) {
                localStorage.setItem('selectedLanguage', orgLang);
                i18n.changeLanguage(orgLang);
            }
        } catch (error) {
            // Skip if org settings are not available for this user type.
        }
    };

    const logout = () => {
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('lastVisitedPath');
        setUser(null);
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                setIsAuthenticated,
                isTransitioning,
                user,
                login,
                loginWithGoogle,
                loginWithToken,
                logout,
                loading,
                refreshUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
